import React, { useContext, useState } from 'react';
import { cloneDeep } from 'es-toolkit/object';

import { UserPreferenceContext } from '@contexts/user-preference-context';

import {
  SalesforceConnection,
  SalesforceConnectionEdition,
  SalesforceConnectionType,
} from '@modules/salesforce/models';
import { SalesforceConnectionsService } from '@modules/salesforce/services';
import {
  MetadataConnectionModalScreens,
  SelectedSalesForceConnection,
  UserConnectionModalScreens,
  useSFConnectionState,
} from '@contexts/sf-connection-context';
import { getConnectionEnv } from '@modules/salesforce/utils/utils';
import { useOAuthFlow } from '@modules/salesforce/shared/hooks/use-oauth-flow';
import { toastify, useTranslation } from '@testsigma/atoms';

export const useCreateConnection = (isGlobalConnection, isTestConnection) => {
  const { t } = useTranslation();
  const [testIsConnecting, setTestIsConnecting] = useState<boolean>();
  const [isConnecting, setIsConnecting] = useState<boolean>();
  const {
    handleUpdateOverlayMode,
    handleUpdateUserConnectionModalScreen,
    selectedConnection,
    handleUpdateSelectedConnection,
    handleReload,
    handleConnectionError,
  } = useSFConnectionState();
  const { userPreference } = useContext(UserPreferenceContext);

  const projectPreference = userPreference?.preference?.projectPreference;

  const applicationVersionId = isGlobalConnection
    ? null
    : userPreference?.preference?.projectPreference?.selectedVersion?.id;

  const handleTestFailure = (error?: string) => {
    const screenName =
      UserConnectionModalScreens.CONNECTION_AUTH_FAILURE_SCREEN;
    handleUpdateUserConnectionModalScreen(screenName);
    handleConnectionError(error);
  };

  const handleTestSuccess = () => {
    const screenName = UserConnectionModalScreens.CONNECTION_TEST_SUCCESSFUL;
    handleUpdateUserConnectionModalScreen(screenName);
  };

  const handleClose = (event: React.SyntheticEvent) => {
    event.preventDefault();
    handleUpdateOverlayMode('none');
    if (selectedConnection?.reload) {
      handleReload();
    }
    handleUpdateSelectedConnection(null);
  };

  const handleSuccess = (sfConnectionData: SalesforceConnection) => {
    setIsConnecting(false);
    handleUpdateUserConnectionModalScreen(
      UserConnectionModalScreens.CONNECTION_SUCCESSFUL_SCREEN,
    );

    const response: SelectedSalesForceConnection = cloneDeep(sfConnectionData);
    response.salesforceConnectionEdition = getConnectionEnv(sfConnectionData);
    response.reload = true;
    response.hasFailed = false;
    handleUpdateSelectedConnection(response);
  };

  const handleFailure = (
    sfConnectionData: SalesforceConnection,
    error: string,
  ) => {
    const response: SelectedSalesForceConnection = cloneDeep(sfConnectionData);
    response.reload = false;
    response.hasFailed = true;
    handleUpdateSelectedConnection(response);
    handleConnectionError(error);

    setIsConnecting(false);
    handleUpdateUserConnectionModalScreen(
      UserConnectionModalScreens.CONNECTION_AUTH_FAILURE_SCREEN,
    );
  };

  const handleTestConnection = (sfConnectionData: SalesforceConnection) => {
    setTestIsConnecting(true);

    const data = cloneDeep(sfConnectionData);

    data.applicationVersionId = applicationVersionId;
    data.performValidation = true;
    data.connectionType = SalesforceConnectionType.METADATA;
    data.isSandbox =
      data.salesforceConnectionEdition === SalesforceConnectionEdition.SANDBOX;
    data.isScratchOrg =
      data.salesforceConnectionEdition ===
      SalesforceConnectionEdition.SCRATCH_ORG;
    data.applicationId = Number(projectPreference?.selectedApplicationId);

    handleUpdateSelectedConnection(data);
    SalesforceConnectionsService.testSFConnection(data)
      .then((response: SalesforceConnection) => {
        if (response?.accessToken) {
          handleTestSuccess();
        } else {
          handleTestFailure();
        }
      })
      .catch((error) => {
        handleTestFailure(error);
      })
      .finally(() => {
        setTestIsConnecting(false);
      });
  };

  const handleCreate = (sfConnectionData: SalesforceConnection) => {
    setIsConnecting(true);

    const data = cloneDeep(sfConnectionData);

    handleUpdateSelectedConnection(data);

    data.applicationVersionId = applicationVersionId;
    data.performValidation = true;
    data.connectionType = SalesforceConnectionType.USER;
    data.isSandbox =
      data.salesforceConnectionEdition === SalesforceConnectionEdition.SANDBOX;
    data.isScratchOrg =
      data.salesforceConnectionEdition ===
      SalesforceConnectionEdition.SCRATCH_ORG;
    data.applicationId = Number(projectPreference?.selectedApplicationId);

    SalesforceConnectionsService.create(data)
      .then(handleSuccess)
      .catch((error) => {
        if (error.response?.status == 422) {
          toastify(t('connectionAlreadyExists', { name: data.name }), {
            type: 'error',
          });
        } else {
          handleFailure(data, error);
        }
      })
      .finally(() => {
        setIsConnecting(false);
      });
  };

  const { handleOAuthLogin, isConnecting: isConnectingOAuth } = useOAuthFlow({
    onAuthComplete: !isTestConnection ? handleCreate : handleTestConnection,
    onAuthError: handleFailure,
  });

  const handleUsernamePasswordLogin = (
    sfConnectionData: SalesforceConnection,
  ) => {
    isTestConnection
      ? handleTestConnection(sfConnectionData)
      : handleCreate(sfConnectionData);
  };

  return {
    isConnecting: isConnectingOAuth || isConnecting,
    hasConnectionFailed: selectedConnection?.hasFailed,
    handleUsernamePasswordLogin,
    handleOAuthLogin,
    handleClose,
    testIsConnecting,
  };
};
